
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { buildDraftKey, useFormDraftStore } from "@/store/formDraftStore";
import { useUserStore } from "@/store/userStore";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { 
  subscriptionFormSchema,
  type SubscriptionFormValues 
} from "../subscriptions/SubscriptionFormSchema";
import { SubscriptionTypeField } from "../subscriptions/SubscriptionTypeField";
import { SubscriptionDateField } from "../subscriptions/SubscriptionDateField";
import { SubscriptionPriceField } from "../subscriptions/SubscriptionPriceField";
import { SubscriptionPaymentMethodField } from "../subscriptions/SubscriptionPaymentMethodField";
import { SubscriptionSessionsField } from "../subscriptions/SubscriptionSessionsField";
import { SubscriptionJokersField } from "../subscriptions/SubscriptionJokersField";
import { SubscriptionPaymentFrequencyField } from "../subscriptions/SubscriptionPaymentFrequencyField";
import { SubscriptionManualActivationField } from "../subscriptions/SubscriptionManualActivationField";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useClientsSupabaseStore } from "@/store/api/clientsSupabaseStore";
import { useEffect, useState } from "react";
import { Client, MembershipType } from "@/types/types";
import { Subscription } from "@/types/subscription";
import { useGymSubscriptionTypes } from "@/hooks/useGymSubscriptionTypes";
import { SubscriptionType } from "@/types/subscriptionType";
import { addDays } from "date-fns";


interface ClientSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
   forceCreateNewSubscriptionOnOpen?: boolean;
}

export function ClientSubscriptionDialog({
  open,
  onOpenChange,
  client,
  forceCreateNewSubscriptionOnOpen,
}: ClientSubscriptionDialogProps) {
  const { toast } = useToast();
  const { t } = useTranslation(["subscriptions", "common"]);
  const { updateSubscriptionOnly, isLoading } = useClientSubscriptionStore();
  const { fetchClientById } = useClientsSupabaseStore();
  const isMobile = useIsMobile();
  const { types: subscriptionTypes, isLoading: typesLoading } = useGymSubscriptionTypes();
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [selectedValidityDays, setSelectedValidityDays] = useState<number | null>(null);
  
  const mostRecentSub = client?.subscriptionHistory?.[0];
  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionFormSchema),
    defaultValues: {
      type: client.membershipType || "Standard",
      price: mostRecentSub?.price?.toString() || "0",
      paymentMethod: mostRecentSub?.payment_method || "cash",
      startDate: new Date(),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
      sessionsTotal: mostRecentSub?.sessions_total || client.sessionsRemaining || 0,
      jokersTotal: mostRecentSub?.jokers_total || client.jokersRemaining || 0,
      paymentFrequency: mostRecentSub?.payment_frequency || "one-time",
      isManuallyActivated: mostRecentSub?.is_manually_activated || false,
      subscriptionDuration: 1
    },
  });
  
  // Brouillon par adhérent (client-subscription:{id}). L'effet reset re-tire
  // à chaque open (et au churn d'identité de mostRecentSub) : mergeDraftOver
  // relit le store en direct et superpose donc toujours le brouillon le plus
  // frais. Les dates repassées par le JSON du stockage revivent en Date.
  const { clearDraft, mergeDraftOver } = useFormDraft<SubscriptionFormValues>(
    "client-subscription",
    form,
    {
      entityId: client?.id != null ? String(client.id) : null,
      restoreOnMount: false,
      revive: (d) => ({
        ...d,
        startDate: d.startDate ? new Date(d.startDate as string) : undefined,
        endDate: d.endDate ? new Date(d.endDate as string) : undefined,
      }) as Partial<SubscriptionFormValues>,
    }
  );
  // Le type sélectionné et sa validité vivent HORS react-hook-form mais
  // participent au submit : clé sœur, restaurée dans le même effet.
  const extraDraftKey = () => {
    const { gymId, user } = useUserStore.getState();
    return buildDraftKey(
      `client-subscription-extra:${client?.id ?? "unknown"}`,
      gymId,
      user?.id ?? null
    );
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTypeId(null);
      setSelectedValidityDays(null);
      form.reset(mergeDraftOver({
        type: client.membershipType || "Standard",
        price: mostRecentSub?.price?.toString() || "0",
        paymentMethod: mostRecentSub?.payment_method || "cash",
        startDate: new Date(),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        sessionsTotal: mostRecentSub?.sessions_total || client.sessionsTotal || 0,
        jokersTotal: mostRecentSub?.jokers_total || client.jokersTotal || 0,
        paymentFrequency: mostRecentSub?.payment_frequency || "one-time",
        isManuallyActivated: mostRecentSub?.is_manually_activated || false,
      }));
      const extra = useFormDraftStore.getState().getDraft(extraDraftKey());
      const saved = extra?.data as { selectedTypeId?: string | null; selectedValidityDays?: number | null } | undefined;
      if (saved) {
        setSelectedTypeId(saved.selectedTypeId ?? null);
        setSelectedValidityDays(saved.selectedValidityDays ?? null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client, mostRecentSub, form, mergeDraftOver]);

  // Sauvegarde de la sélection de type — uniquement après un choix réel.
  useEffect(() => {
    if (!open || selectedTypeId == null) return;
    useFormDraftStore.getState().saveDraft(extraDraftKey(), { selectedTypeId, selectedValidityDays });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeId, selectedValidityDays, open]);

  // Watch startDate changes and recalculate endDate based on validity_days
  const watchedStartDate = form.watch("startDate");
  
  useEffect(() => {
    if (selectedValidityDays && watchedStartDate) {
      const newEndDate = addDays(watchedStartDate, selectedValidityDays);
      form.setValue("endDate", newEndDate);
    }
  }, [watchedStartDate, selectedValidityDays, form]);

  // Handle subscription type selection - auto-populate form fields
  const handleTypeSelect = (subscriptionType: SubscriptionType | null) => {
    if (!subscriptionType) return;
    
    setSelectedTypeId(subscriptionType.id);
    setSelectedValidityDays(subscriptionType.validity_days || null);
    
    // Auto-populate form fields from selected subscription type
    form.setValue("sessionsTotal", subscriptionType.sessions_total);
    form.setValue("jokersTotal", subscriptionType.jokers_total || 0);
    form.setValue("price", subscriptionType.price.toString());
    
    // Calculate end date based on validity_days
    if (subscriptionType.validity_days) {
      const startDate = form.getValues("startDate");
      const endDate = addDays(startDate, subscriptionType.validity_days);
      form.setValue("endDate", endDate);
    }
  };

  const onError = (errors: any) => {
    const firstError = Object.values(errors)[0] as any;
    toast({
      title: t('error', { ns: 'common' }),
      description: firstError?.message || t('errorUpdatingSubscription'),
      variant: "destructive",
      duration: 4000,
    });
  };

  const onSubmit = async (data: SubscriptionFormValues) => {
    // Résolution robuste du type. `selectedTypeId` n'est renseigné que si
    // l'admin CLIQUE le type dans le sélecteur ; or le formulaire pré-remplit
    // `data.type` (le NOM) avec l'abonnement courant du client. Un admin qui
    // valide sans re-cliquer laissait `selectedTypeId` à null → l'abonnement
    // était créé SANS `subscription_type_id`, donc « universel » (il ouvre tous
    // les plannings) → il expirait TOUS les autres abonnements de l'adhérent,
    // y compris ceux d'un planning disjoint. On retrouve donc l'id à partir du
    // nom quand le clic n'a pas eu lieu.
    const normalize = (v: string) => v.trim().toLowerCase();
    const resolvedTypeId =
      selectedTypeId ||
      subscriptionTypes.find((tpe) => normalize(tpe.name) === normalize(data.type))?.id ||
      undefined;

    if (!resolvedTypeId) {
      // Aucun type ne correspond : plutôt que de créer un abonnement universel
      // qui effacerait silencieusement les autres, on refuse et on explique.
      toast({
        title: t('error', { ns: 'common' }),
        description: t('clientSubscriptionDialog.subscriptionTypeRequired', { ns: 'adminui3' }),
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }

    const subscriptionData: Subscription = {
      user_id: client.id,
      type: (data.type) as MembershipType,
      start_date: data.startDate.toISOString(),
      end_date: data.endDate.toISOString(),
      price: parseFloat(data.price),
      status: "Active",
      sessions_total: data.sessionsTotal,
      sessions_remaining: data.sessionsTotal,
      jokers_total: data.jokersTotal,
      jokers_remaining: data.jokersTotal,
      payment_frequency: (data.paymentFrequency || 'one-time') as Subscription['payment_frequency'],
      is_manually_activated: data.isManuallyActivated,
      break_date_start: null,
      break_date_end: null,
      is_paused: false,
      payment_method: data.paymentMethod,
      gym_id: client.gymId,
      subscription_type_id: resolvedTypeId,
    };
    const success = await updateSubscriptionOnly(String(client.id), subscriptionData, forceCreateNewSubscriptionOnOpen);
    
    if (success) {
      const clientName = `${client.firstName} ${client.lastName}`;
      toast({
        title: t('subscriptionUpdated'),
        description: t('subscriptionUpdatedMessage', { name: clientName }),
        duration: 3000,
      });
      
      // Refresh client data after successful update
      if (client.id) {
        fetchClientById(String(client.id));
      }

      clearDraft();
      useFormDraftStore.getState().clearDraft(extraDraftKey());
      onOpenChange(false);
      form.reset();
    } else {
      toast({
        title: t('error', { ns: 'common' }),
        description: t('errorUpdatingSubscription'),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // Adjust the max height to ensure scrolling works properly on all devices
  const maxHeight = isMobile ? "max-h-[60vh]" : "max-h-[65vh]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="pb-2">
          <DialogTitle>{t('renewSubscription')}</DialogTitle>
          <DialogDescription>
            {t('updateDetails', { name: `${client.firstName} ${client.lastName}` })}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className={`flex-1 ${maxHeight} overflow-y-auto pr-4`}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-4 px-1 pb-4">
              <SubscriptionTypeField 
                control={form.control}
                subscriptionTypes={subscriptionTypes}
                onTypeSelect={handleTypeSelect}
              />

              <div className="grid grid-cols-1 gap-4">
                <SubscriptionDateField 
                  control={form.control} 
                  name="startDate" 
                  label={t('startDate')} 
                />
                <SubscriptionDateField 
                  control={form.control} 
                  name="endDate" 
                  label={t('endDate')} 
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <SubscriptionSessionsField control={form.control} />
                <SubscriptionJokersField control={form.control} />
              </div>

              <SubscriptionPriceField control={form.control} />
              {/* <SubscriptionPaymentMethodField control={form.control} />
              <SubscriptionPaymentFrequencyField control={form.control} /> */}
              <SubscriptionManualActivationField control={form.control} />
              
            </form>
          </Form>
        </ScrollArea>

        <DialogFooter className="pt-3 border-t border-border mt-3">
          {/* Annulation explicite : le brouillon (et la sélection de type) est jeté. */}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              clearDraft();
              useFormDraftStore.getState().clearDraft(extraDraftKey());
              onOpenChange(false);
            }}
            disabled={isLoading}
          >
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button type="submit" onClick={form.handleSubmit(onSubmit, onError)} disabled={isLoading || typesLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('processing', { ns: 'common' })}
              </>
            ) : (
              t('save', { ns: 'common' })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
